import clsx from 'clsx';
import { useState } from 'react';
import { AddOrderIcon } from '../icons/add-order';
import { TickIcon } from '../icons/tick';
import { FilterIcon } from '../icons/filter';
import { OrdersIcon } from '../icons/orders';
import { OrderRow } from './order-row';
import { COLS_FULL } from './columns';
import { OrdersSubmission } from './orders-submission';
import { TH } from '../shared/table';
import { NewOrder } from './new-order';
import { Search } from '../shared/search';
import { useOrders } from '../hooks/use-orders';
import { ResLoading } from '../shared/res-loading';
import { Order, OrderStatus } from '../api-new';

export const Orders = () => {
  const { data: orders, isLoading, error } = useOrders();
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [search, setSearch] = useState<Record<keyof Order, string>>();
  const [sorting, setSorting] = useState<{ key: keyof Order; asc: boolean }>({
    key: 'wk_id',
    asc: true,
  });

  if (!orders || isLoading) return <ResLoading />;
  if (error) return <div>Errore: {error.message}</div>;

  const filteredOrders = orders
    .filter((order) =>
      Object.values(order)
        .filter((v) => typeof v === 'string')
        .some((v) =>
          String(v).toLowerCase().includes(globalSearch.toLowerCase()),
        ),
    )
    .filter((order) =>
      Object.entries(search || {}).every(([key, value]) =>
        // @ts-ignore
        order[key].toString().toLowerCase().includes(value.toLowerCase()),
      ),
    )
    .sort((a, b) => {
      const ak = String(a[sorting.key]);
      const bk = String(b[sorting.key]);
      return sorting.asc
        ? ak.localeCompare(bk, 'en', { numeric: true })
        : bk.localeCompare(ak, 'en', { numeric: true });
    });

  const activeFiltersCount =
    Object.values(search || {}).filter((v) => v.length > 0).length +
    (globalSearch.length > 0 ? 1 : 0);

  return (
    <div className="h-full flex flex-col px-8 py-4 gap-4">
      <div className="h-[50px] flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <OrdersIcon className="w-8 h-8 fill-current" />
          Gestione Ordini
        </h1>
        <div className="flex items-center gap-4">
          <span className="text-sm">
            <b>{filteredOrders.length}</b> of {orders.length} ordini
          </span>
          <div
            onClick={() => {
              setSearch(undefined);
              setGlobalSearch('');
            }}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2',
              activeFiltersCount > 0
                ? 'cursor-pointer bg-orange-400 text-white'
                : 'bg-neutral-300 text-neutral-700',
            )}>
            <FilterIcon className="w-5 h-5" active={activeFiltersCount > 0} />{' '}
            Cancella filtri ({activeFiltersCount})
          </div>
          <Search value={globalSearch} onSearch={setGlobalSearch} />
        </div>
      </div>
      <div
        className="overflow-y-auto pr-2"
        style={{ height: 'calc(100vh - 110px)' }}>
        <table className="w-full">
          <thead>
            <tr>
              <TH />
              {COLS_FULL.map((col, i) => (
                <TH
                  key={i}
                  onClick={() => {
                    if (col.sortable) {
                      setSorting({
                        key: col.key,
                        asc: sorting.key === col.key ? !sorting.asc : true,
                      });
                    }
                  }}>
                  <div className="flex items-center justify-between mb-1 cursor-pointer">
                    {col.label}
                    {col.sortable && (
                      <button className="text-xs text-gray-500 flex flex-col gap-0">
                        <span
                          className={clsx(
                            'inline-block leading-[0.5rem] self-end',
                            sorting.key === col.key && sorting.asc
                              ? 'text-black'
                              : 'text-neutral-400',
                          )}>
                          ▲
                        </span>
                        <span
                          className={clsx(
                            'inline-block leading-[0.5rem] self-start',
                            sorting.key === col.key && !sorting.asc
                              ? 'text-black'
                              : 'text-neutral-400',
                          )}>
                          ▼
                        </span>
                      </button>
                    )}
                  </div>

                  {col.searchable && (
                    <div className="relative w-full h-6 bg-red-300">
                      <div className="absolute w-full bg-green-100">
                        <Search
                          size="small"
                          value={search?.[col.key] || ''}
                          onSearch={(v) =>
                            setSearch({
                              ...(search || {}),
                              [col.key]: v,
                            } as Record<keyof Order, string>)
                          }
                        />
                      </div>
                    </div>
                  )}
                </TH>
              ))}
              <TH />
            </tr>
          </thead>
          <tbody>
            {filteredOrders
              // put order.status === imported at the end
              .sort((a, b) => (a.wk_status_code === OrderStatus.READY ? -1 : 1))
              .sort((a, b) =>
                a.wk_status_code === OrderStatus.SCHEDULED ? -1 : 1,
              )
              .sort((a, b) =>
                a.wk_status_code === OrderStatus.CLOSED ? 1 : -1,
              )
              .map((order) => (
                <OrderRow
                  key={order.wk_id}
                  order={order}
                  selected={selectedOrders.includes(order.wk_id)}
                  onSelect={() => {
                    if (selectedOrders.includes(order.wk_id)) {
                      setSelectedOrders(
                        selectedOrders.filter((o) => o !== order.wk_id),
                      );
                    } else {
                      setSelectedOrders([...selectedOrders, order.wk_id]);
                    }
                  }}
                />
              ))}
          </tbody>
        </table>
      </div>
      <div className="h-[60px] flex justify-end gap-4 my-4">
        <div
          className={clsx(
            'transition-all flex items-center gap-4 p-4 text-white bg-blue-500 w-64 rounded-xl font-bold',
            selectedOrders.length === 0
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:scale-105 cursor-pointer',
          )}
          onClick={() => {
            if (selectedOrders.length > 0) {
              setSubmitting(true);
            }
          }}>
          <TickIcon className="w-7 h-7 fill-white border border-white rounded-md p-1" />
          Rivedi Selezionati
        </div>
        <div
          className="cursor-pointer hover:scale-105 transition-all flex items-center gap-4 p-4 text-white bg-green-500 w-64 rounded-xl font-bold"
          onClick={() => setCreating(true)}>
          <AddOrderIcon className="w-6 h-6 fill-white" />
          Nuovo Ordine
        </div>
      </div>
      {submitting && (
        <OrdersSubmission
          orders={orders.filter((o) => selectedOrders.includes(o.wk_id))}
          onClose={() => setSubmitting(false)}
          onRemove={(id) => {
            setSelectedOrders(selectedOrders.filter((o) => o !== id));
          }}
          onSubmit={() => {
            setSelectedOrders([]);
            setSubmitting(false);
          }}
        />
      )}
      {creating && (
        <NewOrder
          onClose={() => setCreating(false)}
          onSubmit={() => {
            setCreating(false);
          }}
        />
      )}
    </div>
  );
};
